import { Component, OnInit, Optional, Inject, ViewChild } from '@angular/core';
import { catchError, debounceTime, distinctUntilChanged, filter, finalize, map, tap, takeUntil } from "rxjs/operators";
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { CampaignsService } from 'src/app/pages/campaign-module/campaigns/campaigns.service';
import { FormGroup, FormBuilder, Validators, FormControl } from '@angular/forms';
import { of as observableOf, Subscription, fromEvent } from 'rxjs';
import { LanguageService, DialogService } from 'src/app/services';
import { environment } from 'src/environments/environment';
import { MediaObserver } from '@angular/flex-layout';
import { requestChain } from 'src/app/model/requests';
import * as _ from 'lodash';

@Component({
  selector: 'esc-branch-maintenance-filter',
  templateUrl: './branch-maintenance-filter.component.html',
  styleUrls: ['./branch-maintenance-filter.component.scss']
})
export class BranchMaintenanceFilterComponent implements OnInit {
  @ViewChild('chainAC') chainACRef: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger) chainACTr: MatAutocompleteTrigger;

  constructor(
    private fb: FormBuilder,
    public media: MediaObserver,
    public campSvc: CampaignsService,
    private dialogService: DialogService,
    private languageService: LanguageService,
    public dialogRef: MatDialogRef<BranchMaintenanceFilterComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any
  ) { }

  public loading = {
    chain: false,
    status: false,
    error: false
  };
  private getDataSub: Subscription;
  public defaultOption = {
    code: "all",
    description: "All"
  };
  chains = [];
  statuses = [
    {
      code: null,
      description: "All",
    },
    {
      code: 1,
      description: "Active",
    },
    {
      code: 0,
      description: "Inactive",
    },
  ];
  public filters;

  public formGroup: FormGroup;
  networkErrMsg = this.languageService.getTranslation("MESSAGE_ERROR_NETWORK_CONNECTION", {});

  ngOnInit() {
    this.filters = _.cloneDeep(requestChain);
    this.initForm();
    this.detectFormChanges();
  }

  initForm() {
    this.formGroup = this.fb.group({
      chain: [_.get(this.data, 'chain') || this.defaultOption, Validators.required],
      status: [_.get(this.data, 'status') || this.statuses[0], Validators.required]
    })
  }

  compareWithFunc = (a: any, b: any) => (a && b) ? a.code == b.code : null;

  detectFormChanges() {
    this.formGroup.get("chain").valueChanges.pipe(
      debounceTime(600),
      filter((term) => !_.isObject(term)),
      distinctUntilChanged(),
      map((term: string) => _.trim(term)),
    )
    .subscribe(value => {
      this.filters.web_filter.keyword = _.toString(value);
      this.chains = [];
      this.getData("chain");
    });
  }

  getInitialData(name) {
    this.autoCmpltClosed(name);
    this.filters = _.cloneDeep(requestChain);
    this.getData(name);
  }
 
  getMoreData(name) {
    if (this.filters.getMore) {
      this.filters.web_filter.offset += requestChain.web_filter.limit;
      this.getData(name);
    }
  }
 
  getData(name) {
    let filters = _.cloneDeep(this.filters)
    this.filters.getMore = false;
    this.loading[name] = true;
 
    switch (name) {
      case "chain": {
        filters.web_filter["chain_type_ids"] = [0];
 
        this.campSvc.getChains(filters).pipe(
          tap(() => this.loading.chain = true),
          finalize(() => {
            this.loading.chain = false
            return observableOf(null);
          }),
          catchError((err) => {
            console.error(err);
            this.loading.chain = false;
            return observableOf(null);
          }))
          .subscribe((res: any) => {
            this.loading.chain = false;
            if (environment.showLogs) {
              console.log(res);
            }
            if (res == null || res.status == 0 || res.status == 504 || res.status == 500) {
              this.loading.error = true;
              this.chains = [];
              this.dialogService.openSnackBar(this.networkErrMsg)
              return;
            }
            if (_.has(res, 'value') && (res.value == "Success")) {
              if(!filters.web_filter.keyword && !this.formGroup.get('chain').value) {
                this.chains[0] = this.defaultOption;
              }

              this.chains = this.chains.concat(res.data);
             
              if((res.data.length > 0) && (this.chains.length < _.get(res, 'count'))) {
                this.filters.getMore = true;
              }
            }
          });
          break;
      }
    }
  }

  autoCmpltClosed(name) {
    let options = (name != 'branch' || name != 'status') ? (name + "s") : (name + "es");
    this[options] = [];
  }

  resetControl(control: FormControl) {
    control.reset(null, { emitEvent: false });
  }
 
  displayFn(data?: any): string | undefined {
    return _.get(data, 'description');
  }
 
  autocompleteScroll(name, refName, acTrigger) {
    setTimeout(() => {
      if (this[refName].isOpen || this[refName].panel) {
        fromEvent(this[refName].panel.nativeElement, 'scroll')
          .pipe(
            map(x => this[refName].panel.nativeElement.scrollTop),
            takeUntil(this[acTrigger].panelClosingActions)
          )
          .subscribe(x => {
            const scrollTop = this[refName].panel.nativeElement
              .scrollTop;
            const scrollHeight = this[refName].panel.nativeElement
              .scrollHeight;
            const elementHeight = this[refName].panel.nativeElement
              .clientHeight;
            const atBottom = scrollHeight === scrollTop + elementHeight;
            if (atBottom) {
              // fetch more data
              this.getMoreData(name);
            }
          });
      }
    });
  }

  applyFilters() {
    for (let ctrl in this.formGroup.controls) {
      if(!this.formGroup.controls[ctrl].valid) {
        this.formGroup.controls[ctrl].markAsTouched();
      }
    }

    if(this.formGroup.valid) {
      this.dialogRef.close(this.formGroup.value);
    }
  }

  reset() {
    for(let ctrl in this.formGroup.controls) {
      this.formGroup.controls[ctrl].reset(null, {emitEvent: false});
      this.formGroup.controls[ctrl].setValue(this.defaultOption, {emitEvent: false});
      this.formGroup.controls[ctrl].markAsUntouched();
    }
    this.formGroup.get('status').setValue(this.statuses[0]);
  }
}